// Справка „Сторно" (Справки → cancellations).
//
// Три гледни точки върху едни и същи редове: списък (както е станало), по
// сервитьор (кой колко отписва спрямо оборота си) и по продукт (кое отпада
// най-често). Изгледът се сменя без нова заявка — `GET /bills/cancellations`
// връща целия период наведнъж, затова таблицата работи в КЛИЕНТСКИ режим.
//
// В списъка редовете, сторнирани с ЕДНО натискане на „Сторнирай всичко", се
// четат като един блок: общ фон, плътна скоба отляво, чип „×N заедно" на
// първия ред и притъмняло име на масата на следващите — иначе едно решение по
// една сметка изглежда като четири отделни.

import { useEffect, useMemo, useState } from "react";
import { App, Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useCancellations } from "../../../hooks/useReports";
import { useUsers } from "../../../hooks/useUsers";
import { useMenuItems, useMenuStations } from "../../../hooks/useMenu";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable, parseDecimal } from "../../../components/dataTable";
import type { ChecklistOption } from "../../../components/dataTable";
import { getBillHistoryById } from "../../../api/reportsBillsHistory";
import type { BillHistory } from "../../../api/reportsBillsHistory";
import ExportExcelButton from "./ExportExcelButton";
import BillOperationsModal from "./BillOperationsModal";
import { ReportTopBar } from "./ReportTopBar";
import { previousPeriodOf } from "./reportPeriod";
import { CancellationsKpis } from "./cancellations/CancellationsKpis";
import {
  CancellationsTabs,
  type CancellationsView,
} from "./cancellations/CancellationsTabs";
import {
  batchBandRowIds,
  groupByProduct,
  groupByWaiter,
  summarize,
} from "./cancellations/cancellationsMath";
import type {
  NamedCancelledItem,
  ProductVoidRow,
  WaiterVoidRow,
} from "./cancellations/cancellationsMath";
import {
  useCancellationsListColumns,
  useCancellationsProductColumns,
  useCancellationsWaiterColumns,
} from "./cancellations/cancellationsColumns";
import { exportCancellations } from "./cancellations/cancellationsExport";
import { pluralLabel } from "./reportPlural";
import {
  businessDateOf,
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const CancellationsReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;
  const { message } = App.useApp();
  const langCode = i18n.language?.startsWith("en") ? "en" : "bg";

  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  // Изключено по подразбиране: справката отговаря първо на въпроса „колко
  // е сега", а Δ-тата са втори въпрос и струват втора заявка за период,
  // който човек не е поискал.
  const [compare, setCompare] = useState(false);
  const [view, setView] = useState<CancellationsView>("list");
  const [searchText, setSearchText] = useState("");
  const [selectedBill, setSelectedBill] = useState<BillHistory | null>(null);

  // Редовете СЛЕД филтър и подредба — Excel изнася видяното, не суровия набор.
  // Таблицата ги подава оттук, защото само тя знае какво е останало след
  // филтрите в колоните.
  const [visibleList, setVisibleList] = useState<NamedCancelledItem[]>([]);
  const [visibleWaiter, setVisibleWaiter] = useState<WaiterVoidRow[]>([]);
  const [visibleProduct, setVisibleProduct] = useState<ProductVoidRow[]>([]);

  const { data: users = [] } = useUsers();
  const menuItems = useMenuItems();
  const stations = useMenuStations();

  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      start_date: dateRange[0].format("YYYY-MM-DD HH:mm:ss"),
      end_date: dateRange[1].format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [dateRange]);

  // Предходният равен по дължина период — само за Δ чиповете. Тече ли още
  // избраният период, краят се подрязва до изтеклото (виж `reportPeriod`),
  // иначе непълен ден стои срещу пълен.
  const previousFilters = useMemo(() => {
    if (!compare || !dateRange?.[0] || !dateRange?.[1]) return null;
    const previous = previousPeriodOf(dateRange[0], dateRange[1]);
    return {
      start_date: previous.start.format("YYYY-MM-DD HH:mm:ss"),
      end_date: previous.end.format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [compare, dateRange]);

  const { data: items = [], isLoading, isFetching, refetch } =
    useCancellations(filters);
  // `useCancellations` няма `enabled` — при изключено сравнение просто се
  // пита за същия период и резултатът не се ползва (React Query го дава от
  // кеша, без втора мрежова заявка).
  const { data: previousItems = [] } = useCancellations(
    previousFilters ?? filters,
  );

  const nameOf = useMemo(() => {
    const waiters = new Map<number, string>();
    for (const user of users) {
      waiters.set(user.id, user.names || user.username || String(user.id));
    }
    const itemNames = new Map<number, string>();
    for (const menuItem of menuItems) {
      const translated = menuItem.itemLangs.find(
        (lang) => lang.languageCode === langCode,
      );
      itemNames.set(menuItem.id, translated?.name || menuItem.slug);
    }
    const stationNames = new Map<number, string>();
    for (const station of stations) {
      const translated =
        station.translations.find(
          (translation) => translation.languageCode === langCode,
        )?.name ?? station.translations[0]?.name;
      stationNames.set(station.id, translated ?? `#${station.id}`);
    }
    return {
      waiterName: (id: number | null | undefined) =>
        id === null || id === undefined ? "—" : (waiters.get(id) ?? `#${id}`),
      itemName: (id: number) => itemNames.get(id) ?? `#${id}`,
      stationName: (id: number) => stationNames.get(id) ?? `#${id}`,
    };
  }, [users, menuItems, stations, langCode]);

  // Имената влизат в САМИТЕ редове. Ако се резолвваха в `accessorFn`,
  // TanStack щеше да ги закове при първия рендер (кеш на клетката за живота
  // на `data`) и меню, пристигнало по-късно, нямаше да се появи никога.
  const namedItems = useMemo<NamedCancelledItem[]>(
    () =>
      items.map((item) => ({
        ...item,
        productName: nameOf.itemName(item.item_id),
        waiterName: nameOf.waiterName(item.waiter_id),
        cancelledByName: nameOf.waiterName(item.cancelled_by_user_id),
      })),
    [items, nameOf],
  );

  // Търсачката гледа всичко, което човек чете в реда: маса, продукт,
  // сервитьор, кой е сторнирал и причината. Филтърът е тук, а не в колона,
  // защото въпросът обикновено е „намери ми онази сметка", не „стесни колона".
  const searchedItems = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return namedItems;
    return namedItems.filter((item) =>
      [
        item.bill_name,
        item.productName,
        item.waiterName,
        item.cancelledByName,
        item.reason ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [namedItems, searchText]);

  const summary = useMemo(() => summarize(items), [items]);
  const previousSummary = useMemo(
    () => (previousFilters ? summarize(previousItems) : null),
    [previousFilters, previousItems],
  );
  const byWaiter = useMemo(() => groupByWaiter(items), [items]);
  const byProduct = useMemo(() => groupByProduct(items), [items]);

  // Оборотът за периода идва по редовете (справката е зад `view_voids` и не
  // може да пита „История на маси") — една и съща стойност навсякъде.
  const revenue = items.length ? parseDecimal(items[0].period_revenue) : 0;

  // Показателят „след кухня" се появява само ако има ЗАПИСАН етап.
  const showKitchen = summary.rows > 0 && summary.kitchenUnknown < summary.rows;

  const isMultiDay = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return false;
    // По РАБОТНА дата: краят на работния ден е 05:59 на следващата календарна
    // дата и суровото сравнение обявява един ден за период.
    return !businessDateOf(dateRange[0]).isSame(
      businessDateOf(dateRange[1]),
      "day",
    );
  }, [dateRange]);

  // Опциите се вадят от СЪЩОТО поле, което клетката рисува (`waiter_id`).
  // Падането към „кой е натиснал" даваше опции, които не хващат нито един ред.
  // Ключът е самият етикет, защото филтърът сравнява показания текст —
  // затова се дедуплицира по етикет, не по id.
  const waiterOptions = useMemo<ChecklistOption[]>(() => {
    const seen = new Set<string>();
    for (const item of items) {
      seen.add(nameOf.waiterName(item.waiter_id ?? null));
    }
    return Array.from(seen)
      .map((label) => ({ value: label, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, nameOf]);

  const cancelledByOptions = useMemo<ChecklistOption[]>(() => {
    const seen = new Set<string>();
    for (const item of items) {
      seen.add(nameOf.waiterName(item.cancelled_by_user_id));
    }
    return Array.from(seen)
      .map((label) => ({ value: label, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [items, nameOf]);

  const listColumns = useCancellationsListColumns(
    waiterOptions,
    cancelledByOptions,
    isMultiDay,
  );
  const waiterColumns = useCancellationsWaiterColumns(nameOf, showKitchen);
  const productColumns = useCancellationsProductColumns(nameOf, showKitchen);

  // Общият фон на партидата се смята върху ФИЛТРИРАНИТЕ И ПОДРЕДЕНИ редове —
  // те са единственото, което таблицата вдига навън (`onFilteredRowsChange`).
  // Разкъсаните партиди остават без фон: две несъседни сторна от различни
  // сметки, паднали едно до друго след сортиране, биха се слели в една лента.
  const bandedRowIds = useMemo(
    () => batchBandRowIds(visibleList),
    [visibleList],
  );

  // Смяната на изглед нулира избраната сметка — модалът виси иначе.
  // (Самите таблици се пресъздават по `key` — виж рендера долу.)
  useEffect(() => setSelectedBill(null), [view]);

  const openBill = async (billId: number) => {
    try {
      const bill = await getBillHistoryById(billId, "reports");
      // Липсваща сметка е същият отказ както грешка по мрежата — тихото
      // „нищо не се случи" оставя човека да натиска реда пак и пак.
      if (bill) setSelectedBill(bill);
      else message.error(t("reportsPage.voidsBillOpenFailed"));
    } catch {
      message.error(t("reportsPage.voidsBillOpenFailed"));
    }
  };

  const handleExport = () =>
    exportCancellations(
      view,
      { list: visibleList, waiter: visibleWaiter, product: visibleProduct },
      { t, ...nameOf },
    );

  const shownCount =
    view === "list"
      ? visibleList.length
      : view === "waiter"
        ? visibleWaiter.length
        : visibleProduct.length;

  const countLabel = pluralLabel(
    t,
    shownCount,
    "reportsPage.voidsRowOne",
    "reportsPage.voidsRowMany",
  );

  const hint =
    view === "list"
      ? t("reportsPage.voidsListHint")
      : view === "waiter"
        ? t("reportsPage.voidsWaiterHint")
        : t("reportsPage.voidsProductHint");

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.cancellations")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {countLabel}
        <span className="font-medium text-muted">{` · ${hint}`}</span>
      </span>
    </div>
  );

  const emptyProps = {
    emptyTitle: t("reportsPage.voidsEmptyTitle"),
    emptyHint: t("reportsPage.voidsEmptyHint"),
  };

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.cancellationsTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
            <button
              type="button"
              onClick={() => setCompare((previous) => !previous)}
              aria-pressed={compare}
              className={`h-9 shrink-0 rounded-lg border px-3.5 text-[12.5px] font-bold transition-colors ${
                compare
                  ? "border-brand-tint2 bg-brand-tint text-brand-deep-ink"
                  : "border-shade-3 bg-surface text-soft hover:bg-fill"
              }`}
            >
              {compare ? "✓ " : ""}
              {t("reportsPage.billsHistoryComparePrevious")}
            </button>
          </div>

          <CancellationsTabs
            value={view}
            onChange={setView}
            labels={{
              list: t("reportsPage.voidsViewList"),
              waiter: t("reportsPage.voidsByWaiter"),
              product: t("reportsPage.voidsByProduct"),
            }}
          />

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <CancellationsKpis
                summary={summary}
                previous={previousSummary}
                revenue={revenue}
                isNarrow={narrow}
              />

              {view === "list" ? (
                <DataTable<NamedCancelledItem>
                  key="list"
                  tableId="cancellations-list"
                  data={searchedItems}
                  columns={listColumns}
                  getRowId={(row) => String(row.id)}
                  refreshing={isFetching}
                  ariaLabel={t("reportsPage.cancellationsTitle")}
                  title={tableTitle}
                  search={{
                    value: searchText,
                    onChange: setSearchText,
                    placeholder: t("reportsPage.voidsSearchPlaceholder"),
                  }}
                  onFilteredRowsChange={setVisibleList}
                  toolbarRight={
                    <ExportExcelButton
                      onExport={handleExport}
                      disabled={shownCount === 0}
                      bare
                    />
                  }
                  onRowClick={(row) => openBill(row.bill_id)}
                  // Бракът остава червен и в партида: неговият тон казва друго
                  // и не бива да се губи под фона на групата. Скобата минава и
                  // през такъв ред, така че блокът пак се чете цял.
                  rowTone={(row) =>
                    row.is_defective
                      ? "danger"
                      : bandedRowIds.has(row.id)
                        ? "brand"
                        : null
                  }
                  initialSorting={[{ id: "cancelled_at", desc: true }]}
                  {...emptyProps}
                />
              ) : view === "waiter" ? (
                <DataTable
                  key="waiter"
                  tableId="cancellations-waiter"
                  data={byWaiter}
                  columns={waiterColumns}
                  getRowId={(row) => String(row.waiterId)}
                  refreshing={isFetching}
                  ariaLabel={t("reportsPage.voidsByWaiter")}
                  title={tableTitle}
                  onFilteredRowsChange={setVisibleWaiter}
                  toolbarRight={
                    <ExportExcelButton
                      onExport={handleExport}
                      disabled={shownCount === 0}
                      bare
                    />
                  }
                  initialSorting={[{ id: "total", desc: true }]}
                  {...emptyProps}
                />
              ) : (
                <DataTable
                  key="product"
                  tableId="cancellations-product"
                  data={byProduct}
                  columns={productColumns}
                  getRowId={(row) => String(row.itemId)}
                  refreshing={isFetching}
                  ariaLabel={t("reportsPage.voidsByProduct")}
                  title={tableTitle}
                  onFilteredRowsChange={setVisibleProduct}
                  toolbarRight={
                    <ExportExcelButton
                      onExport={handleExport}
                      disabled={shownCount === 0}
                      bare
                    />
                  }
                  initialSorting={[{ id: "total", desc: true }]}
                  {...emptyProps}
                />
              )}
            </>
          )}
        </div>
      </div>

      {selectedBill ? (
        <BillOperationsModal
          bill={selectedBill}
          open
          onClose={() => setSelectedBill(null)}
          // `navigate(0)` презареждаше цялото приложение — `refetch()` стига.
          onBillUpdated={() => refetch()}
          // БЕЗ `showCancelButton`: тази справка е за четене.
        />
      ) : null}
    </div>
  );
};
